import { useCallback, useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import { registryAssetsModel } from '@/entities/registryAssets';

import {
  LANGUAGE_FILTER_ALL,
  SOURCE_FILTER_ALL,
  TEMPLATES_PAGE_SIZE,
  UseCommunityTemplatesArgs,
  UseCommunityTemplatesResult,
} from '../config';

export const useCommunityTemplates = (
  args: UseCommunityTemplatesArgs,
): UseCommunityTemplatesResult => {
  const { organizationId, params } = args;
  const { query, source, language } = params;

  const dispatch = useAppDispatch();

  const items = useAppSelector(registryAssetsModel.selectors.selectItems);
  const isLoading = useAppSelector(
    registryAssetsModel.selectors.selectIsLoading,
  );
  const isLoadingMore = useAppSelector(
    registryAssetsModel.selectors.selectIsLoadingMore,
  );
  const error = useAppSelector(registryAssetsModel.selectors.selectError);
  const loadedCount = useAppSelector(
    registryAssetsModel.selectors.selectLoadedCount,
  );
  const hasMore = useAppSelector(registryAssetsModel.selectors.selectHasMore);
  const availableLanguages = useAppSelector(
    registryAssetsModel.selectors.selectAvailableLanguages,
  );

  const inFlightRef = useRef<{ abort: () => void } | null>(null);

  const dispatchFetch = useCallback(
    (
      offset: number,
      mode: registryAssetsModel.thunks.FetchRegistryAssetsMode,
    ) => {
      const promise = dispatch(
        registryAssetsModel.thunks.fetchRegistryAssets({
          organizationId,
          family: 'document_template',
          sourceKind: source === SOURCE_FILTER_ALL ? undefined : source,
          query: query || undefined,
          language: language === LANGUAGE_FILTER_ALL ? undefined : language,
          offset,
          limit: TEMPLATES_PAGE_SIZE,
          mode,
        }),
      );

      inFlightRef.current = promise;
      promise.finally(() => {
        if (inFlightRef.current === promise) {
          inFlightRef.current = null;
        }
      });
    },
    [dispatch, organizationId, query, source, language],
  );

  useEffect(() => {
    if (!organizationId) {
      return;
    }

    dispatchFetch(0, 'replace');

    return () => {
      inFlightRef.current?.abort();
      inFlightRef.current = null;
    };
  }, [dispatchFetch, organizationId]);

  const loadMore = useCallback(() => {
    if (!organizationId || !hasMore || inFlightRef.current) {
      return;
    }

    dispatchFetch(loadedCount, 'append');
  }, [dispatchFetch, organizationId, hasMore, loadedCount]);

  return {
    availableLanguages,
    error,
    hasMore,
    isLoading,
    isLoadingMore,
    items,
    loadMore,
  };
};
